import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { reportClientEvent } from '@/shared/lib/report-client-event';
import {
  hardReloadApp,
  hasChunkReloadBeenAttempted,
  markChunkReloadAttempted,
} from './chunk-reload';
import css from './error-boundary.module.scss';
import { isChunkLoadError } from './is-chunk-load-error';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
  /** True when the thrown error looks like a post-deploy chunk miss. */
  isChunkError: boolean;
};

/**
 * Root error boundary. For ordinary render bugs shows a refresh CTA.
 * For failed dynamic imports after a deploy (XT-413 / XT-282):
 * - first hit outside the cooldown window → hard-reload once (cache-bust)
 * - further hits inside the cooldown → "Reload app" UI only (no loop)
 *
 * Emits debounced `POST /client-events` diagnostics for BE analysis.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught error:', error, info.componentStack);

    const chunk = isChunkLoadError(error);
    const alreadyReloaded = hasChunkReloadBeenAttempted();

    reportClientEvent({
      kind: chunk ? 'chunk_load_error' : 'render_error',
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      alreadyReloaded: chunk ? alreadyReloaded : undefined,
      meta: {
        chunkError: chunk,
      },
    });

    if (!chunk) return;
    if (alreadyReloaded) return;

    markChunkReloadAttempted();
    reportClientEvent({
      kind: 'chunk_reload_auto',
      name: error.name,
      message: error.message,
      alreadyReloaded: false,
    });
    hardReloadApp();
  }

  private handleReload = () => {
    reportClientEvent({
      kind: 'chunk_reload_manual',
      message: 'User clicked Reload app',
      alreadyReloaded: hasChunkReloadBeenAttempted(),
    });
    markChunkReloadAttempted();
    hardReloadApp();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      // While an automatic hard-reload is in flight, avoid flashing the
      // generic copy for a chunk miss — show the update message instead.
      if (this.state.isChunkError) {
        return (
          <div className={css.root}>
            <h2 className={css.title}>App update available</h2>
            <p className={css.message}>
              A new version of Xthory was deployed. Reload to load the latest
              app (this fixes missing page scripts after an update).
            </p>
            <button
              className={css.button}
              onClick={this.handleReload}
              type="button"
            >
              Reload app
            </button>
          </div>
        );
      }

      return (
        <div className={css.root}>
          <h2 className={css.title}>Something went wrong</h2>
          <p className={css.message}>
            An unexpected error occurred. Please refresh the page.
          </p>
          <button
            className={css.button}
            onClick={() => window.location.reload()}
            type="button"
          >
            Refresh page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
